import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { IDLE, PENDING, DANGER, SUCCESS } from "const"
import { CcLibrarySet } from "server/models"
import { StateType } from "client/utils"
import {
    StoreType,
    CcLibrarySetImportFilters,

    LoadAllCcLibrarySets,
    LoadCcLibrarySetsReadyForImport,
    SortCcLibrarySets,
    GetMaxCcLibrarySets,

    setCcLibrarySetImportFromLibraryList,
    setCcLibrarySetImportFromLibraryIds,
    setCcLibrarySetImportFromLibrarySort,
    setCcLibrarySetImportFromLibraryFilters,

    setCcLibrarySetImportFromAccountList,
    setCcLibrarySetImportFromAccountIds,
    setCcLibrarySetImportFromAccountSort,
    setCcLibrarySetImportFromAccountFilters,

    setCcLibrarySetReloadList,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageMenu,
    PageMenuButton,
    Tab,
    PageForm,
    Feedback,
    Loader,
    TableSearchWrapper,
    Table,
    SortRows,
} from "client/components"

import CcLibrarySetImportSearch from "client/pages/search/ccLibrarySetImportSearch"
import Columns, { LibraryToAccountSet } from "./columns"

const ACCOUNT = "#account"
const LIBRARY = "#library"

const ImportSetList = () => {
    const project = useSelector((state: StoreType) => state.project.projectView)
    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const reloadImported = useSelector((state: StoreType) => state.ccLibrarySet.reloadImported)

    const librarySetList = useSelector((state: StoreType) => state.ccLibrarySet.importFromLibrary.setList)
    const librarySetIds = useSelector((state: StoreType) => state.ccLibrarySet.importFromLibrary.setIds)
    const librarySortColumn = useSelector((state: StoreType) => state.ccLibrarySet.importFromLibrary.sortColumn)
    const librarySortOrder = useSelector((state: StoreType) => state.ccLibrarySet.importFromLibrary.sortOrder)
    const libraryFilters = useSelector((state: StoreType) => state.ccLibrarySet.importFromLibrary.filters)

    const accountSetList = useSelector((state: StoreType) => state.ccLibrarySet.importFromAccount.setList)
    const accountSetIds = useSelector((state: StoreType) => state.ccLibrarySet.importFromAccount.setIds)
    const accountSortColumn = useSelector((state: StoreType) => state.ccLibrarySet.importFromAccount.sortColumn)
    const accountSortOrder = useSelector((state: StoreType) => state.ccLibrarySet.importFromAccount.sortOrder)
    const accountFilters = useSelector((state: StoreType) => state.ccLibrarySet.importFromAccount.filters)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [importedFromLibraryToAccount, setImportedFromLibraryToAccount] = useState<LibraryToAccountSet>({})
    const [importedFromAccountToProject, setImportedFromAccountToProject] = useState<number[]>([])
    const [activeTab, setActiveTab] = useState(ACCOUNT)
    const [loadState, setLoadState] = useState<StateType>(IDLE)

    // on component unmount
    useEffect(() => (() => {
        dispatch(setCcLibrarySetReloadList(false))
        setTimeout(() => {
            dispatch(setCcLibrarySetReloadList(true))
        }, 0)
    }), [])

    useEffect(() => {
        if (!reloadImported) return

        setLoadState(PENDING)
        const promiseArray: Promise<any>[] = []
        const fromLibraryToAccount: LibraryToAccountSet = {}
        const fromAccountToProject: number[] = []

        promiseArray.push(
            LoadAllCcLibrarySets(userData.accountId, null).then((res) => {
                Object.values(res).forEach((set: CcLibrarySet) => {
                    if (set.originalSetId) fromLibraryToAccount[set.originalSetId] = set.id
                })
                setImportedFromLibraryToAccount(fromLibraryToAccount)
            }),
            LoadCcLibrarySetsReadyForImport(userData.accountId, 0).then((res) => {
                dispatch(setCcLibrarySetImportFromLibraryList(res))
            }),
        )

        if (project.id) {
            promiseArray.push(
                LoadAllCcLibrarySets(userData.accountId, project.id).then((res) => {
                    Object.values(res).forEach((set: CcLibrarySet) => {
                        if (set.originalSetId) fromAccountToProject.push(set.originalSetId)
                    })
                    setImportedFromAccountToProject(fromAccountToProject)
                }),
                LoadCcLibrarySetsReadyForImport(userData.accountId, 1).then((res) => {
                    dispatch(setCcLibrarySetImportFromAccountList(res))
                }),
            )
        }

        Promise.all(promiseArray).then(() => {
            setLoadState(IDLE)
        })
    }, [reloadImported])

    useEffect(() => {
        const sortedIds = SortCcLibrarySets(Object.values(librarySetList), librarySortColumn, librarySortOrder, libraryFilters)
        dispatch(setCcLibrarySetImportFromLibraryIds(sortedIds))
    }, [librarySetList, librarySortColumn, librarySortOrder, libraryFilters])

    useEffect(() => {
        const sortedIds = SortCcLibrarySets(Object.values(accountSetList), accountSortColumn, accountSortOrder, accountFilters)
        dispatch(setCcLibrarySetImportFromAccountIds(sortedIds))
    }, [accountSetList, accountSortColumn, accountSortOrder, accountFilters])

    const onSortImportFromLibrary: SortRows = (column: keyof CcLibrarySet, order) => {
        dispatch(setCcLibrarySetImportFromLibrarySort(column, order))
    }

    const onSortImportFromAccount: SortRows = (column: keyof CcLibrarySet, order) => {
        dispatch(setCcLibrarySetImportFromAccountSort(column, order))
    }

    const onFilterImportFromLibrary = (newFilters: CcLibrarySetImportFilters) => {
        dispatch(setCcLibrarySetImportFromLibraryFilters(newFilters))
    }

    const onFilterImportFromAccount = (newFilters: CcLibrarySetImportFilters) => {
        dispatch(setCcLibrarySetImportFromAccountFilters(newFilters))
    }

    // max number of library sets
    const {
        maxLibrarySets,
        feedbackLibrarySets,
    } = GetMaxCcLibrarySets(packageSettings, userData, undefined, Object.keys(importedFromLibraryToAccount).length, lang)

    const maxLibrarySetsExceeded = maxLibrarySets !== null && Object.keys(importedFromLibraryToAccount).length > maxLibrarySets

    const importFromLibraryColumns = Columns(
        importedFromLibraryToAccount,
        importedFromAccountToProject,
        "library",
        maxLibrarySets,
    )
    const importFromLibrarySearch = (
        <TableSearchWrapper>
            <CcLibrarySetImportSearch
                id="importFromLibrary"
                filters={libraryFilters}
                onFilter={onFilterImportFromLibrary}
            />
        </TableSearchWrapper>
    )
    const importFromLibraryTable = (
        <Table
            columns={importFromLibraryColumns}
            dataList={librarySetList}
            dataIds={librarySetIds}
            sortColumn={librarySortColumn}
            sortOrder={librarySortOrder}
            rowSize="md"
            showPagination
            onSort={onSortImportFromLibrary}
        />
    )
    const importFromLibraryFeedback = (
        <PageForm>
            <Feedback variant={maxLibrarySetsExceeded ? DANGER : SUCCESS}>{feedbackLibrarySets}</Feedback>
        </PageForm>
    )

    const importFromAccountColumns = Columns(
        null,
        importedFromAccountToProject,
        "account",
        null,
    )
    const importFromAccountSearch = (
        <TableSearchWrapper>
            <CcLibrarySetImportSearch
                id="importFromAccount"
                filters={accountFilters}
                onFilter={onFilterImportFromAccount}
            />
        </TableSearchWrapper>
    )
    const importFromAccountTable = (
        <Table
            columns={importFromAccountColumns}
            dataList={accountSetList}
            dataIds={accountSetIds}
            sortColumn={accountSortColumn}
            sortOrder={accountSortOrder}
            rowSize="md"
            showPagination
            onSort={onSortImportFromAccount}
        />
    )

    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.importComplianceSets}</PageTitle>
            </PageHeader>
            {
                !project.id && (
                    <PageContent>
                        { loadState === PENDING && <Loader /> }
                        { importFromLibrarySearch }
                        { importFromLibraryTable }
                        { importFromLibraryFeedback }
                    </PageContent>
                )
            }
            {
                project.id && (
                    <PageContent>
                        <PageMenu>
                            <PageMenuButton
                                isActive={activeTab === ACCOUNT}
                                onClick={() => setActiveTab(ACCOUNT)}
                            >
                                {lang.account}
                            </PageMenuButton>
                            <PageMenuButton
                                isActive={activeTab === LIBRARY}
                                onClick={() => setActiveTab(LIBRARY)}
                            >
                                {lang.library}
                            </PageMenuButton>
                        </PageMenu>
                        <Tab show={activeTab === ACCOUNT}>
                            { loadState === PENDING && <Loader /> }
                            { importFromAccountSearch }
                            { importFromAccountTable }
                        </Tab>
                        <Tab show={activeTab === LIBRARY}>
                            { loadState === PENDING && <Loader /> }
                            { importFromLibrarySearch }
                            { importFromLibraryTable }
                            { importFromLibraryFeedback }
                        </Tab>
                    </PageContent>
                )
            }
        </Container>
    )
}
export default ImportSetList
